<template>
    <div class="row">
        <div class="col-md-9">
            <select v-model="step" class="form-control form-control-sm mb-1 w-auto" @change="getResults">
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
            </select>
            <LineChartGenerator
                v-if="loaded && chartData"
                :chart-options="chartOptions"
                :chart-data="chartData"
            />
            <LineChartGenerator
                v-if="loaded && chartDataComparison"
                :chart-options="chartOptions"
                :chart-data="chartDataComparison"
            />
        </div>
        <div class="col-md">
            <div>Data</div>
            <select v-model="dataFilterType" class="form-control mb-3 text-capitalize" @change="changeDataFilters()">
                <option value="global">Global</option>
                <option value="product">Product</option>
                <option value="category">Category</option>
                <option value="location">{{config.default_country ? 'Cities' : 'Countries'}}</option>
                <option value="abandoned_carts" v-if="config.modules.abandoned_carts">Abandoned carts</option>
            </select>
            <div v-if="dataFilterType == 'product'">
                <div>Products to compare</div>
                <select v-model="dataSelectedProducts" class="form-control mb-3" multiple @change="getResults">
                    <option value="all">All</option>
                    <option v-for="product in dataProducts" :key="product.id" :value="product.id">{{product.nameWithVariation}}</option>
                </select>
            </div>
            <div v-if="dataFilterType == 'category'">
                <div>Categories to compare</div>
                <select v-model="dataSelectedCategories" class="form-control mb-3" multiple @change="getResults">
                    <option value="all">All</option>
                    <option v-for="category in dataCategories" :key="category.id" :value="category.id">{{category.name}}</option>
                </select>
            </div>
            <div v-if="dataFilterType == 'location'">
                <div><span class="text-capitalize">{{ config.default_country ? 'Cities' : 'Countries' }}</span> to compare</div>
                <select v-model="dataSelectedLocations" class="form-control mb-3" multiple @change="getResults">
                    <option value="all">All</option>
                    <option v-for="location in dataLocations" :key="location" :value="location">
                        {{config.default_country ? location : country_list[location]}}
                    </option>
                </select>
            </div>
            <div>Data type</div>
            <select v-if="dataFilterType == 'abandoned_carts'" v-model="dataType" class="form-control mb-3" @change="getResults">
                <option value="Lost turnover">Lost turnover</option>
                <option value="Quantity">Quantity</option>
                <option value="Number of carts">Number of carts</option>
                <option value="Average cart amount">Average cart amount</option>
            </select>
            <select v-else v-model="dataType" class="form-control mb-3" @change="getResults">
                <option value="Turnover">Turnover</option>
                <option value="Quantity">Quantity</option>
                <option value="Number of orders">Number of orders</option>
                <option value="Average order amount">Average order amount</option>
            </select>
            <div>Date range</div>
            <div class="d-flex">
                <date-range-picker
                    class="w-100"
                    controlContainerClass="form-control"
                    ref="picker"
                    @update="getResults"
                    v-model="dateRange"
                    opens="left"
                    :autoApply="true"
                    :locale-data="{ firstDay: 1, format: 'yyyy-mm-dd' }"
                    :showDropdowns="true"
                ></date-range-picker>
                <button v-if="dateRange.startDate && dateRange.endDate" class="btn btn-link btn-sm" @click="clear">Clear</button>
            </div>
            <div v-if="dateRange.startDate && dateRange.endDate">
                <div class="btn btn-link btn-sm px-0"
                @click="compareWithPreviousPeriod">Compare with a previous period <i class="la la-caret-down"></i></div>
                <transition name="fade">
                    <div v-if="showDateRangeComparison">
                        <div>Date range to compare</div>
                        <div class="d-flex">
                            <date-range-picker
                                class="w-100"
                                controlContainerClass="form-control"
                                ref="picker"
                                @update="(dateRange.startDate && dateRange.endDate ? getResults() : false)"
                                v-model="dateRangeComparison"
                                opens="left"
                                :autoApply="true"
                                :locale-data="{ firstDay: 1, format: 'yyyy-mm-dd' }"
                                :showDropdowns="true"
                            ></date-range-picker>
                            <button v-if="dateRangeComparison.startDate && dateRangeComparison.endDate" class="btn btn-link btn-sm" @click="clearComparison">Clear</button>
                        </div>
                    </div>
                </transition>
            </div>
            <div class="btn btn-secondary mt-3" @click="exportExcel"><i class="las la-download"></i> Export (.xls)</div>
        </div>
    </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
//you need to import the CSS manually
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { Line as LineChartGenerator } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement
} from 'chart.js'

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement
);

export default {
    name: "LineChart",
    components: { LineChartGenerator, DateRangePicker },
    props: ['dataProducts', 'dataCategories', 'dataLocations'],
    data() {
        return {
            config: window.config,
            t: window.t,
            country_list: window.country_list,
            loaded: false,
            dataType: 'Turnover',
            dateRange: {
                startDate: null,
                endDate: null,
            },
            dateRangeComparison: {
                startDate: null,
                endDate: null,
            },
            showDateRangeComparison: false,
            step: 'month',
            dataFilterType: 'global',
            dataSelectedProducts: [],
            dataSelectedCategories: [],
            dataSelectedLocations: [],
            chartData: null,
            chartDataComparison: null,
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
            }
        }
    },
    mounted() {
        this.getResults()
    },
    methods: {
        changeDataFilters() {
            this.dataSelectedProducts = this.dataSelectedCategories = this.dataSelectedLocations = []
            if (this.dataFilterType == 'abandoned_carts') {
                this.dataType = 'Lost turnover'
            } else {
                this.dataType = 'Turnover'
            }
            if (this.dataFilterType == 'global' || this.dataFilterType == 'abandoned_carts') {
                this.getResults()
            }
        },
        params(action) {
            return {
                action: action,
                dataFilterType: this.dataFilterType,
                dataProducts: this.dataSelectedProducts,
                dataCategories: this.dataSelectedCategories,
                dataLocations: this.dataSelectedLocations,
                dataType: this.dataType,
                startDate: this.dateRange.startDate ? this.dateRange.startDate.toISOString().split('T')[0] : 0,
                endDate: this.dateRange.endDate ?  this.dateRange.endDate.toISOString().split('T')[0] : 0,
                startDateComparison: this.dateRangeComparison.startDate ? this.dateRangeComparison.startDate.toISOString().split('T')[0] : 0,
                endDateComparison: this.dateRangeComparison.endDate ?  this.dateRangeComparison.endDate.toISOString().split('T')[0] : 0,
                step: this.step
            }
        },
        async getResults() {
            if (this.dataSelectedProducts.find((value) => value == 'all')) {
                this.dataSelectedProducts = ['all']
            }
            if (this.dataSelectedCategories.find((value) => value == 'all')) {
                this.dataSelectedCategories = ['all']
            }
            if (this.dataSelectedLocations.find((value) => value == 'all')) {
                this.dataSelectedLocations = ['all']
            }
            this.dataFilterType
            axios.post("/admin/ajax/statistics", this.params('chart'))
            .catch((error) => {
                new Noty({
                    type: "error",
                    text: "Operation failed",
                }).show();
            })
            .then((response) => {
                this.chartDataComparison = null
                this.chartData = response.data.chartData
                if (response.data.chartDataComparison) {
                    this.chartDataComparison = response.data.chartDataComparison
                }
                this.loaded = true
            });
        },
        compareWithPreviousPeriod() {
            this.showDateRangeComparison = this.showDateRangeComparison ? false : true;
            if (this.showDateRangeComparison) {
                this.dateRangeComparison = {
                    startDate: this.dateRange.startDate,
                    endDate: this.dateRange.endDate
                }
                this.getResults()
            } else {
                this.clearComparison()
            }
        },
        clear() {
            this.dateRange = this.dateRangeComparison = {
                startDate: null,
                endDate: null,
            }
            this.showDateRangeComparison = false
            this.getResults()
        },
        clearComparison() {
            this.dateRangeComparison = {
                startDate: null,
                endDate: null,
            }
            this.chartDataComparison = null
            this.getResults()
        },
        exportExcel() {
            window.open('/admin/statistics/export?'+this.convertToQueryUrl(this.params('export')), '_blank')
        },
    }
};
</script>
